import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  ButtonInputWithPopover,
  CardItem,
  DataGrid,
  DebouncedSearchInput,
  ErrorMessage,
  GridColDef,
  GridRowId,
  GridRowParams,
  InfoMessage,
  LoadingSpinner,
  Modal,
  useFormatting,
  useScreenWidth,
} from "@msys/ui";
import {
  OpenInNew as OpenInNewIcon,
  Replay as ReplayIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Tab,
  TablePagination,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import type { TranslationKey } from "@msys/tolgee";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import {
  PdsSyncEntityType,
  PdsSyncTaskAction,
  PdsSyncTaskFilter,
  PdsSyncTaskStatus,
} from "../../../clients/graphqlTypes.js";
import {
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { RESULTS_PER_PAGE_OPTIONS } from "../../constants.js";
import { useCrmContactTypes } from "../crm/useCrmContactTypes.js";
import { PdsSyncReviewAccess } from "./usePdsSyncReviewAccess.js";
import {
  PdsSyncReviewModal_TasksDocument,
  PdsSyncReviewModal_TasksQuery,
  PdsSyncReviewModal_TasksQueryVariables,
  PdsSyncReviewTaskFragment,
  usePdsSyncReviewModal_AppliedTodayCountQuery,
  usePdsSyncReviewModal_ApproveTasksMutation,
  usePdsSyncReviewModal_RejectTasksMutation,
  usePdsSyncReviewModal_RetryTaskMutation,
  usePdsSyncReviewModal_TasksQuery,
} from "./PdsSyncReviewModal.generated.js";

const PAGE_SIZE = 25;

// Compact grid footer. The pagination labels are <p> elements whose default
// 14px margins make the toolbar 48px tall; against a 40px toolbar and
// TablePagination's overflow:auto root that renders a stray scrollbar. Reset
// the margins so the content fits, and keep minHeight (not height) so a longer
// label grows the toolbar instead of scrolling it.
const COMPACT_FOOTER_SX = {
  "& .MuiDataGrid-footerContainer": { minHeight: 40 },
  "& .MuiTablePagination-toolbar": { minHeight: 40 },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    margin: 0,
  },
} as const;

const HISTORY_STATUSES: PdsSyncTaskStatus[] = [
  "AUTO_APPROVED",
  "APPROVED",
  "REJECTED",
  "EXECUTED",
  "FAILED",
];

type TabValue = "toReview" | "history";

function crmDetailPath(task: {
  entityType: PdsSyncEntityType;
  targetId?: string | null;
}): string | null {
  if (!task.targetId) return null;
  return task.entityType === "CONTACT"
    ? `/crm/users/${task.targetId}`
    : `/crm/organisations/${task.targetId}`;
}

/**
 * Multi-select filter control (button + popover), styled like the Documents >
 * Quotes filter idiom. Used for Status (server array), Type (2 options mapping
 * losslessly onto the single `entityType` server field — see call sites) and
 * Change (client-side action filter). An empty selection means "all".
 */
function PdsSyncMultiChoiceFilter<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: readonly T[];
  options: { value: T; label: string }[];
  onChange: (value: T[]) => void;
}) {
  return (
    <ButtonInputWithPopover
      label={label}
      counter={
        value.length > 0 && value.length < options.length
          ? value.length
          : undefined
      }
      popoverWidth="md"
      content={
        <Box sx={{ p: 1 }}>
          <FormControl component="fieldset" variant="standard">
            <FormGroup>
              {options.map(option => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={value.includes(option.value)}
                      onChange={(_event, checked) => {
                        if (checked) {
                          onChange(
                            Array.from(new Set([...value, option.value]))
                          );
                        } else {
                          onChange(value.filter(v => v !== option.value));
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body1">{option.label}</Typography>
                  }
                  sx={{ ml: 0, mr: 0 }}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Box>
      }
    />
  );
}

interface Props {
  handleClose: () => void;
  access: PdsSyncReviewAccess;
  defaultEntityType: PdsSyncEntityType;
  initialTab?: TabValue;
  initialStatusFilter?: readonly PdsSyncTaskStatus[];
}

/**
 * "Edits from PDS" — the single global review modal (design D3). Built with a
 * third (outbound crm_to_pds) tab in mind: state-based tabs, not routes, so
 * adding one later needs no rename.
 */
export function PdsSyncReviewModal({
  handleClose,
  access,
  defaultEntityType,
  initialTab = "history",
  initialStatusFilter,
}: Props) {
  const { t } = useTranslate(["Integrations", "Global"]);
  const { isMaxPhone } = useScreenWidth();
  const [tab, setTab] = React.useState<TabValue>(initialTab);

  // Review flow is hidden for now (designer review): the modal is a log-only
  // view. Flip this to true to bring back the tabbed To-review / History UI —
  // the To-review panel and Tabs code below stay intact, just gated.
  const REVIEW_ENABLED = false;

  if (!access.isEnabled) return null;

  const lastSyncLabel = access.lastSyncAt
    ? moment(access.lastSyncAt).fromNow(true)
    : null;

  return (
    <Modal
      title={t("PDS sync log", { ns: "Integrations" })}
      handleClose={handleClose}
      maxWidth="lg"
      // The @msys/ui Modal auto-full-screens on phones, so only reserve the
      // fixed 85vh paper height on larger screens (avoids a cramped dialog).
      dialogProps={
        isMaxPhone
          ? undefined
          : { slotProps: { paper: { style: { height: "85vh" } } } }
      }
      alwaysVisible
    >
      <Stack
        direction="column"
        spacing={1}
        sx={{ height: "100%", overflow: "hidden" }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          {lastSyncLabel && (
            <>
              <Typography variant="caption" color="text.secondary">
                {t("Last sync {time} ago", {
                  ns: "Integrations",
                  time: lastSyncLabel,
                })}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  lineHeight: 1,
                  color: "text.primary",
                }}
              >
                ·
              </Typography>
            </>
          )}
          {REVIEW_ENABLED && (
            <>
              <Typography variant="caption" color="text.secondary">
                {t("{count} to review", {
                  ns: "Integrations",
                  count: access.counts?.pending ?? 0,
                })}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  lineHeight: 1,
                  color: "text.primary",
                }}
              >
                ·
              </Typography>
            </>
          )}
          <PdsSyncAppliedTodayStat />
        </Stack>

        {REVIEW_ENABLED && (
          <Tabs
            value={tab}
            onChange={(_event: React.SyntheticEvent, newValue: TabValue) =>
              setTab(newValue)
            }
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            sx={theme => ({
              minHeight: "unset",
              [".MuiTab-root"]: {
                minHeight: "unset",
                minWidth: "unset",
                padding: "6px 12px",
                borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
                ["&:hover:not(.Mui-selected)"]: {
                  backgroundColor: theme.palette.action.hover,
                },
                ["&:active:not(.Mui-selected)"]: {
                  backgroundColor: theme.palette.action.selected,
                },
                ["&.Mui-selected"]: {
                  color: theme.palette.primary.main,
                },
              },
            })}
          >
            <Tab
              value="toReview"
              label={t("To review", { ns: "Integrations" })}
            />
            <Tab value="history" label={t("History", { ns: "Integrations" })} />
          </Tabs>
        )}

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {REVIEW_ENABLED && tab === "toReview" ? (
            <PdsSyncToReviewPanel
              defaultEntityType={defaultEntityType}
              onMutated={access.refetch}
            />
          ) : (
            <PdsSyncHistoryPanel
              defaultEntityType={defaultEntityType}
              initialStatusFilter={initialStatusFilter}
            />
          )}
        </Box>
      </Stack>
    </Modal>
  );
}

/**
 * "{m} applied today" (design D3 stats strip) — an exact backend count of
 * tasks executed since the client's local start-of-day. Previously derived
 * client-side from a max-100 EXECUTED sample, which capped the displayed
 * number at 100 (MEIS-12819 QA-1).
 */
export function PdsSyncAppliedTodayStat() {
  const { t } = useTranslate(["Integrations"]);
  const client = useApolloClient();
  // Client-local day boundary on purpose — keeps the pre-existing timezone
  // semantics instead of a server-side "today".
  const executedSince = React.useMemo(
    () => moment().startOf("day").toISOString(),
    []
  );
  const query = usePdsSyncReviewModal_AppliedTodayCountQuery({
    client,
    fetchPolicy: "cache-and-network",
    variables: { executedSince },
  });

  const appliedToday =
    getDataOrNull(query.data?.pdsSyncTaskCounts)?.executedSince ?? 0;

  return (
    <Typography variant="caption" color="text.secondary">
      {t("{count} applied today", { ns: "Integrations", count: appliedToday })}
    </Typography>
  );
}

// -- "To review" tab --

export function PdsSyncToReviewPanel({
  defaultEntityType,
  onMutated,
}: {
  defaultEntityType: PdsSyncEntityType;
  onMutated: () => void;
}) {
  const { t } = useTranslate(["Integrations", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const { isMinTablet } = useScreenWidth();
  const { getFormattedDateTime } = useFormatting();
  const client = useApolloClient();
  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const { contactTypeLabels } = useCrmContactTypes();

  const [entityTypes, setEntityTypes] = React.useState<PdsSyncEntityType[]>([
    defaultEntityType,
  ]);
  const [actions, setActions] = React.useState<PdsSyncTaskAction[]>([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = React.useState(false);
  const [busyIds, setBusyIds] = React.useState<Set<string>>(new Set());
  const [expandedRowIds, setExpandedRowIds] = React.useState<Set<GridRowId>>(
    new Set()
  );

  const toggleCardSelection = React.useCallback((id: string) => {
    setSelectAllMatching(false);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const resetSelection = React.useCallback(() => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  }, []);

  React.useEffect(() => {
    setPaginationModel(pm => ({ ...pm, page: 0 }));
    resetSelection();
  }, [entityTypes, actions, resetSelection]);

  // `entityType` is a single server field: exactly one selected → filter by
  // it; 0 or both selected → omit (i.e. all types).
  const filter: PdsSyncTaskFilter = {
    status: ["PENDING"],
    entityType: entityTypes.length === 1 ? entityTypes[0] : undefined,
  };

  const query = usePdsSyncReviewModal_TasksQuery({
    client,
    fetchPolicy: "cache-and-network",
    variables: {
      filter,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    },
  });

  const [approveTasks] = usePdsSyncReviewModal_ApproveTasksMutation({ client });
  const [rejectTasks] = usePdsSyncReviewModal_RejectTasksMutation({ client });

  const serverItems =
    getDataOrNull(query.data?.pdsSyncTasks)?.items ??
    getDataOrNull(query.previousData?.pdsSyncTasks)?.items ??
    [];
  const totalCount =
    getDataOrNull(query.data?.pdsSyncTasks)?.totalCount ??
    getDataOrNull(query.previousData?.pdsSyncTasks)?.totalCount ??
    0;

  // "Change" and free-text search have no server-side filter param
  // (PdsSyncTaskFilter only has status/direction/entityType) — filtered
  // client-side over the current page. Change has exactly two options, so
  // selecting one narrows; 0 or both means no action filtering.
  const actionFilterActive = actions.length > 0 && actions.length < 2;
  const hasLocalFilter = actionFilterActive || searchTerm.trim() !== "";
  const items = React.useMemo(() => {
    let filtered = serverItems;
    if (actions.length > 0 && actions.length < 2) {
      filtered = filtered.filter(i => actions.includes(i.action));
    }
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(i =>
        (i.displayName ?? "").toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [serverItems, actions, searchTerm]);

  const showPositiveEmpty = totalCount === 0 && !hasLocalFilter;
  const showFilteredEmpty =
    !showPositiveEmpty && !query.loading && !query.error && items.length === 0;

  async function resolveTargetTaskIds(): Promise<string[]> {
    if (!selectAllMatching) return Array.from(selectedIds);
    const result = await client.query<
      PdsSyncReviewModal_TasksQuery,
      PdsSyncReviewModal_TasksQueryVariables
    >({
      query: PdsSyncReviewModal_TasksDocument,
      variables: { filter, limit: totalCount, offset: 0 },
      fetchPolicy: "network-only",
    });
    return (
      getDataOrNull(result.data.pdsSyncTasks)?.items.map(item => item.id) ?? []
    );
  }

  async function refresh() {
    await query.refetch();
    onMutated();
  }

  async function handleApplyOne(
    task: PdsSyncReviewTaskFragment,
    { skipConfirm = false }: { skipConfirm?: boolean } = {}
  ) {
    if (!skipConfirm) {
      const confirmed = await confirmProcessRef.current?.startConfirmProcess({
        title: t("Apply this change?", { ns: "Integrations" }),
        text: t(
          "This change will be applied directly to your contacts and cannot be automatically undone.",
          { ns: "Integrations" }
        ),
        confirmButtonLabel: t("Apply", { ns: "Integrations" }),
      });
      if (!confirmed) return;
    }
    setBusyIds(prev => new Set(prev).add(task.id));
    try {
      const result = await approveTasks({ variables: { taskIds: [task.id] } });
      const failed = result.data?.approvePdsSyncTasks.failed ?? [];
      if (failed.length > 0) {
        enqueueSnackbar(
          t("The action could not be completed. Please try again.", {
            ns: "Integrations",
          }),
          { variant: "error" }
        );
      } else {
        const path = crmDetailPath(task);
        enqueueSnackbar(
          t("Change released — will be applied shortly", {
            ns: "Integrations",
          }),
          {
            variant: "success",
            action: path
              ? () => (
                  <Button
                    color="inherit"
                    size="small"
                    component="a"
                    href={path}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("Open contact", { ns: "Integrations" })}
                  </Button>
                )
              : undefined,
          }
        );
      }
      await refresh();
    } catch (e) {
      enqueueSnackbar(
        t("The action could not be completed. Please try again.", {
          ns: "Integrations",
        }),
        { variant: "error" }
      );
    } finally {
      setBusyIds(prev => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  }

  async function handleRejectOne(
    task: PdsSyncReviewTaskFragment,
    { skipConfirm = false }: { skipConfirm?: boolean } = {}
  ) {
    if (!skipConfirm) {
      // Single-row reject confirm uses its own singular title (aligned with the
      // single-apply "Apply this change?"), distinct from the bulk count title.
      const confirmed = await confirmProcessRef.current?.startConfirmProcess({
        title: t("Reject this change?", { ns: "Integrations" }),
        text: t(
          "This change will not be applied. If the contact changes again in PDS, a new task will appear.",
          { ns: "Integrations" }
        ),
        confirmButtonLabel: t("Reject", { ns: "Integrations" }),
      });
      if (!confirmed) return;
    }
    setBusyIds(prev => new Set(prev).add(task.id));
    try {
      await rejectTasks({ variables: { taskIds: [task.id] } });
      await refresh();
    } catch (e) {
      enqueueSnackbar(
        t("The action could not be completed. Please try again.", {
          ns: "Integrations",
        }),
        { variant: "error" }
      );
    } finally {
      setBusyIds(prev => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  }

  async function handleBulkApply() {
    const ids = await resolveTargetTaskIds();
    if (ids.length === 0) return;
    const confirmed = await confirmProcessRef.current?.startConfirmProcess({
      title: t("{count} changes to apply?", {
        ns: "Integrations",
        count: ids.length,
      }),
      text: t(
        "This change will be applied directly to your contacts and cannot be automatically undone.",
        { ns: "Integrations" }
      ),
      confirmButtonLabel: t("Apply", { ns: "Integrations" }),
    });
    if (!confirmed) return;
    try {
      const result = await approveTasks({ variables: { taskIds: ids } });
      const succeeded = result.data?.approvePdsSyncTasks.succeeded ?? 0;
      const failed = result.data?.approvePdsSyncTasks.failed ?? [];
      enqueueSnackbar(
        t("{succeeded} released, {skipped} skipped", {
          ns: "Integrations",
          succeeded,
          skipped: failed.length,
        }),
        { variant: failed.length > 0 ? "warning" : "success" }
      );
      resetSelection();
      await refresh();
    } catch (e) {
      enqueueSnackbar(
        t("The action could not be completed. Please try again.", {
          ns: "Integrations",
        }),
        { variant: "error" }
      );
    }
  }

  async function handleBulkReject() {
    const ids = await resolveTargetTaskIds();
    if (ids.length === 0) return;
    const confirmed = await confirmProcessRef.current?.startConfirmProcess({
      title: t("{count} changes to reject?", {
        ns: "Integrations",
        count: ids.length,
      }),
      text: t(
        "This change will not be applied. If the contact changes again in PDS, a new task will appear.",
        { ns: "Integrations" }
      ),
      confirmButtonLabel: t("Reject", { ns: "Integrations" }),
    });
    if (!confirmed) return;
    try {
      await rejectTasks({ variables: { taskIds: ids } });
      resetSelection();
      await refresh();
    } catch (e) {
      enqueueSnackbar(
        t("The action could not be completed. Please try again.", {
          ns: "Integrations",
        }),
        { variant: "error" }
      );
    }
  }

  const columns = React.useMemo(
    (): GridColDef<PdsSyncReviewTaskFragment>[] => [
      {
        field: "displayName",
        headerName: t("Contact", { ns: "Integrations" }),
        flex: 2,
        minWidth: 140,
        // server-side sort deferred to MEIS-12759 (dual-source displayName)
        sortable: false,
        renderCell: ({ row }) =>
          busyIds.has(row.id) ? (
            <Typography variant="body2" color="text.secondary">
              {t("Applying…", { ns: "Integrations" })}
            </Typography>
          ) : (
            <Stack spacing={0}>
              <Typography variant="body2" className="sentry-mask">
                {row.displayName ?? "—"}
              </Typography>
              {row.entityType === "CONTACT" && !row.dependsOnResolved && (
                <Typography variant="caption" color="text.secondary">
                  {t("Waiting for company {name}", {
                    ns: "Integrations",
                    name: row.dependsOnSourceIds[0] ?? "",
                  })}
                </Typography>
              )}
            </Stack>
          ),
      },
      {
        field: "entityType",
        headerName: t("Type", { ns: "Integrations" }),
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            label={
              contactTypeLabels[
                row.entityType === "CONTACT" ? "INDIVIDUAL" : "COMPANY"
              ]
            }
          />
        ),
      },
      {
        field: "action",
        headerName: t("Change", { ns: "Integrations" }),
        width: 100,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            color={row.action === "CREATE" ? "success" : undefined}
            label={
              row.action === "CREATE"
                ? t("New", { ns: "Integrations" })
                : row.action === "UPDATE"
                  ? t("Edit", { ns: "Integrations" })
                  : row.action
            }
          />
        ),
      },
      {
        field: "fields",
        headerName: t("Fields", { ns: "Integrations" }),
        flex: 2,
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) =>
          row.fieldChanges
            .map(fc =>
              t(fc.label as TranslationKey<"Integrations">, {
                ns: "Integrations",
              })
            )
            .join(", "),
      },
      {
        field: "createdAt",
        headerName: t("Detected", { ns: "Integrations" }),
        width: 160,
        sortable: false,
        renderCell: ({ value }) => getFormattedDateTime(value as string),
      },
      {
        field: "open",
        headerName: "",
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const path = crmDetailPath(row);
          if (!path) return null;
          return (
            <Tooltip
              title={
                row.entityType === "COMPANY"
                  ? t("Open company", { ns: "Integrations" })
                  : t("Open contact", { ns: "Integrations" })
              }
            >
              <IconButton
                size="small"
                color="primary"
                component="a"
                href={path}
                target="_blank"
                rel="noreferrer"
                onClick={e => e.stopPropagation()}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [t, busyIds, contactTypeLabels, getFormattedDateTime]
  );

  const rowSelectionModel = React.useMemo(
    () => ({
      type: "include" as const,
      ids: selectAllMatching ? new Set(items.map(i => i.id)) : selectedIds,
    }),
    [selectAllMatching, items, selectedIds]
  );

  const selectedCount = selectAllMatching ? totalCount : selectedIds.size;
  const canOfferSelectAllMatching =
    !hasLocalFilter &&
    selectedIds.size === items.length &&
    items.length > 0 &&
    totalCount > items.length;

  return (
    <Stack direction="column" spacing={1} sx={{ height: "100%" }}>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: "center", flexWrap: "wrap" }}
      >
        <PdsSyncMultiChoiceFilter<PdsSyncEntityType>
          label={t("Type", { ns: "Integrations" })}
          value={entityTypes}
          onChange={setEntityTypes}
          options={[
            { value: "CONTACT", label: contactTypeLabels.INDIVIDUAL },
            { value: "COMPANY", label: contactTypeLabels.COMPANY },
          ]}
        />
        <PdsSyncMultiChoiceFilter<PdsSyncTaskAction>
          label={t("Change", { ns: "Integrations" })}
          value={actions}
          onChange={setActions}
          options={[
            { value: "CREATE", label: t("New", { ns: "Integrations" }) },
            { value: "UPDATE", label: t("Edit", { ns: "Integrations" }) },
          ]}
        />
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <DebouncedSearchInput
            placeholder={t("Search", { ns: "Global" })}
            defaultValue={searchTerm}
            onChangeSearchTerm={setSearchTerm}
          />
        </Box>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {query.error ? (
          <ErrorMessage
            message={t("Could not load. Please try again.", {
              ns: "Integrations",
            })}
            button={
              <Button onClick={() => query.refetch()} variant="outlined">
                {t("Retry", { ns: "Integrations" })}
              </Button>
            }
          />
        ) : showPositiveEmpty ? (
          <InfoMessage
            message={t("Nothing to review — all edits from PDS are applied.", {
              ns: "Integrations",
            })}
          />
        ) : showFilteredEmpty ? (
          <InfoMessage
            message={t("No matches for the current filters.", {
              ns: "Integrations",
            })}
          />
        ) : query.loading && items.length === 0 ? (
          <LoadingSpinner />
        ) : isMinTablet ? (
          <DataGrid<PdsSyncReviewTaskFragment>
            density="compact"
            sx={COMPACT_FOOTER_SX}
            localeText={{
              noRowsLabel: t("There are no items to display", { ns: "Global" }),
            }}
            loading={query.loading}
            hideFooter={totalCount === 0}
            columns={columns}
            rows={items}
            checkboxSelection
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={newModel => {
              setSelectAllMatching(false);
              setSelectedIds(new Set(Array.from(newModel.ids).map(String)));
            }}
            disableRowSelectionExcludeModel
            keepNonExistentRowsSelected
            // Whole-row click-to-expand instead of a "+" toggle column: hide
            // the default detail-panel toggle column and drive expansion via
            // the controlled detailPanelExpandedRowIds set.
            columnVisibilityModel={{
              [GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field]: false,
            }}
            onRowClick={({ id }: GridRowParams<PdsSyncReviewTaskFragment>) =>
              setExpandedRowIds(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            detailPanelExpandedRowIds={expandedRowIds}
            onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
            rowCount={totalCount}
            getDetailPanelContent={({
              row,
            }: GridRowParams<PdsSyncReviewTaskFragment>) => (
              <PdsSyncRowDetail
                row={row}
                busy={busyIds.has(row.id)}
                onApply={() => handleApplyOne(row)}
                onReject={() => handleRejectOne(row)}
                selected={selectAllMatching || selectedIds.has(row.id)}
              />
            )}
            getDetailPanelHeight={() => "auto" as const}
          />
        ) : (
          <Stack direction="column" spacing={1}>
            {items.map(row => {
              const path = crmDetailPath(row);
              const expanded = expandedRowIds.has(row.id);
              return (
                <CardItem key={row.id}>
                  <Stack direction="column" spacing={1}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "flex-start" }}
                    >
                      <Checkbox
                        size="small"
                        sx={{ mt: -1, ml: -1 }}
                        checked={selectAllMatching || selectedIds.has(row.id)}
                        onClick={e => e.stopPropagation()}
                        onChange={() => toggleCardSelection(row.id)}
                      />
                      <Stack
                        direction="column"
                        spacing={0.5}
                        sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                        onClick={() =>
                          setExpandedRowIds(prev => {
                            const next = new Set(prev);
                            if (next.has(row.id)) next.delete(row.id);
                            else next.add(row.id);
                            return next;
                          })
                        }
                      >
                        <Typography variant="body2" className="sentry-mask">
                          {row.displayName ?? "—"}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          useFlexGap
                          sx={{ flexWrap: "wrap" }}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              contactTypeLabels[
                                row.entityType === "CONTACT"
                                  ? "INDIVIDUAL"
                                  : "COMPANY"
                              ]
                            }
                          />
                          <Chip
                            size="small"
                            color={
                              row.action === "CREATE" ? "success" : undefined
                            }
                            label={
                              row.action === "CREATE"
                                ? t("New", { ns: "Integrations" })
                                : row.action === "UPDATE"
                                  ? t("Edit", { ns: "Integrations" })
                                  : row.action
                            }
                          />
                        </Stack>
                      </Stack>
                      {path && (
                        <Tooltip
                          title={
                            row.entityType === "COMPANY"
                              ? t("Open company", { ns: "Integrations" })
                              : t("Open contact", { ns: "Integrations" })
                          }
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ mt: -1, mr: -1 }}
                            component="a"
                            href={path}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    {expanded && (
                      <PdsSyncRowDetail
                        row={row}
                        busy={busyIds.has(row.id)}
                        onApply={() => handleApplyOne(row)}
                        onReject={() => handleRejectOne(row)}
                        selected={selectAllMatching || selectedIds.has(row.id)}
                      />
                    )}
                  </Stack>
                </CardItem>
              );
            })}
            <TablePagination
              component="div"
              count={totalCount}
              page={paginationModel.page}
              onPageChange={(_event, newPage) =>
                setPaginationModel(pm => ({ ...pm, page: newPage }))
              }
              rowsPerPage={paginationModel.pageSize}
              onRowsPerPageChange={event =>
                setPaginationModel({
                  pageSize: parseInt(event.target.value, 10),
                  page: 0,
                })
              }
              labelRowsPerPage={t("Per page:", { ns: "Global" })}
            />
          </Stack>
        )}
      </Box>

      {/* Persistent bulk-action footer: always rendered so it reserves its
          space (no layout shift when a selection appears). */}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{
          minHeight: 44,
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectAllMatching
            ? t("All {count} selected", {
                ns: "Integrations",
                count: selectedCount,
              })
            : t("{count} selected", {
                ns: "Integrations",
                count: selectedCount,
              })}
        </Typography>
        {canOfferSelectAllMatching && (
          <Button
            size="small"
            variant="text"
            onClick={() => setSelectAllMatching(true)}
          >
            {t("Select all {count}", {
              ns: "Integrations",
              count: totalCount,
            })}
          </Button>
        )}
        <Button
          size="small"
          variant="text"
          disabled={selectedCount === 0}
          onClick={resetSelection}
        >
          {t("Clear selection", { ns: "Integrations" })}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={selectedCount === 0}
          onClick={handleBulkReject}
        >
          {t("Reject", { ns: "Integrations" })}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={selectedCount === 0}
          onClick={handleBulkApply}
        >
          {t("Apply", { ns: "Integrations" })}
        </Button>
      </Stack>

      <ConfirmProcess ref={confirmProcessRef} />
    </Stack>
  );
}

function PdsSyncRowDetail({
  row,
  busy,
  onApply,
  onReject,
  selected,
}: {
  row: PdsSyncReviewTaskFragment;
  busy: boolean;
  onApply: () => void;
  onReject: () => void;
  selected?: boolean;
}) {
  const { t } = useTranslate(["Integrations", "Global"]);

  return (
    <Box
      sx={theme => ({
        p: 2,
        // Match the DataGrid's native selected-row tint (primary-tinted, not
        // the grey `action.selected`) so the expanded area reads as selected.
        ...(selected
          ? {
              backgroundColor: alpha(
                theme.palette.primary.main,
                theme.palette.action.selectedOpacity
              ),
            }
          : {}),
      })}
    >
      <Stack spacing={1}>
        {row.fieldChanges.length > 0 && (
          <Stack direction="row" spacing={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                minWidth: { xs: 96, sm: 180 },
                flexShrink: 0,
                fontWeight: 500,
              }}
            >
              {t("Field", { ns: "Integrations" })}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {t("New value", { ns: "Integrations" })}
            </Typography>
          </Stack>
        )}
        {row.fieldChanges.map(fc => (
          <Stack
            key={fc.field}
            direction="row"
            spacing={1}
            sx={{ alignItems: "flex-start" }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: { xs: 96, sm: 180 }, flexShrink: 0 }}
            >
              {t(fc.label as TranslationKey<"Integrations">, {
                ns: "Integrations",
              })}
            </Typography>
            <Typography
              variant="body2"
              className="sentry-mask"
              sx={{
                flex: 1,
                minWidth: 0,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {fc.newValue ?? "—"}
            </Typography>
          </Stack>
        ))}
        {row.entityType === "CONTACT" && !row.dependsOnResolved && (
          <Typography variant="caption" color="text.secondary">
            {t("Waiting for company {name}", {
              ns: "Integrations",
              name: row.dependsOnSourceIds[0] ?? "",
            })}
          </Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={busy}
            onClick={onReject}
          >
            {t("Reject", { ns: "Integrations" })}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={busy}
            onClick={onApply}
          >
            {t("Apply", { ns: "Integrations" })}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

// -- "History" tab --

export function PdsSyncHistoryPanel({
  defaultEntityType,
  initialStatusFilter,
}: {
  defaultEntityType: PdsSyncEntityType;
  initialStatusFilter?: readonly PdsSyncTaskStatus[];
}) {
  const { t } = useTranslate(["Integrations", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const { isMinTablet } = useScreenWidth();
  const { getFormattedDateTime } = useFormatting();
  const client = useApolloClient();
  const { contactTypeLabels } = useCrmContactTypes();

  const [entityTypes, setEntityTypes] = React.useState<PdsSyncEntityType[]>([
    defaultEntityType,
  ]);
  const [status, setStatus] = React.useState<PdsSyncTaskStatus[]>(
    initialStatusFilter
      ? initialStatusFilter.filter(s => HISTORY_STATUSES.includes(s))
      : []
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [retryingIds, setRetryingIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    setPaginationModel(pm => ({ ...pm, page: 0 }));
  }, [entityTypes, status]);

  // `entityType` is a single server field: exactly one selected → filter by
  // it; 0 or both selected → omit (i.e. all types).
  const filter: PdsSyncTaskFilter = {
    status: status.length > 0 ? status : HISTORY_STATUSES,
    entityType: entityTypes.length === 1 ? entityTypes[0] : undefined,
  };

  const query = usePdsSyncReviewModal_TasksQuery({
    client,
    fetchPolicy: "cache-and-network",
    variables: {
      filter,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    },
  });

  const [retryTask] = usePdsSyncReviewModal_RetryTaskMutation({ client });

  const serverItems =
    getDataOrNull(query.data?.pdsSyncTasks)?.items ??
    getDataOrNull(query.previousData?.pdsSyncTasks)?.items ??
    [];
  const totalCount =
    getDataOrNull(query.data?.pdsSyncTasks)?.totalCount ??
    getDataOrNull(query.previousData?.pdsSyncTasks)?.totalCount ??
    0;

  const hasLocalFilter = searchTerm.trim() !== "";
  const items = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return serverItems;
    return serverItems.filter(i =>
      (i.displayName ?? "").toLowerCase().includes(term)
    );
  }, [serverItems, searchTerm]);

  const showEmpty = totalCount === 0 && !hasLocalFilter && status.length === 0;
  const showFilteredEmpty =
    !showEmpty && !query.loading && !query.error && items.length === 0;

  const STATUS_LABELS: Record<PdsSyncTaskStatus, string> = {
    PENDING: t("Pending", { ns: "Integrations" }),
    AUTO_APPROVED: t("Auto-approved", { ns: "Integrations" }),
    APPROVED: t("Approved", { ns: "Integrations" }),
    REJECTED: t("Rejected", { ns: "Integrations" }),
    EXECUTED: t("Applied", { ns: "Integrations" }),
    FAILED: t("Failed", { ns: "Integrations" }),
  };

  async function handleRetry(task: PdsSyncReviewTaskFragment) {
    setRetryingIds(prev => new Set(prev).add(task.id));
    try {
      await retryTask({ variables: { taskId: task.id } });
      await query.refetch();
    } catch (e) {
      enqueueSnackbar(
        t("The action could not be completed. Please try again.", {
          ns: "Integrations",
        }),
        { variant: "error" }
      );
    } finally {
      setRetryingIds(prev => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  }

  const columns = React.useMemo(
    (): GridColDef<PdsSyncReviewTaskFragment>[] => [
      {
        field: "displayName",
        headerName: t("Contact", { ns: "Integrations" }),
        flex: 2,
        minWidth: 140,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography variant="body2" className="sentry-mask">
            {row.displayName ?? "—"}
          </Typography>
        ),
      },
      {
        field: "entityType",
        headerName: t("Type", { ns: "Integrations" }),
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            label={
              contactTypeLabels[
                row.entityType === "CONTACT" ? "INDIVIDUAL" : "COMPANY"
              ]
            }
          />
        ),
      },
      {
        field: "status",
        headerName: t("Status", { ns: "Integrations" }),
        width: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            color={
              row.status === "FAILED"
                ? "error"
                : row.status === "REJECTED"
                  ? "default"
                  : "success"
            }
            label={STATUS_LABELS[row.status]}
          />
        ),
      },
      {
        field: "updatedAt",
        headerName: t("Timestamp", { ns: "Integrations" }),
        width: 160,
        sortable: false,
        renderCell: ({ value }) => getFormattedDateTime(value as string),
      },
      {
        field: "actions",
        headerName: t("Actions", { ns: "Global" }),
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) =>
          row.status === "FAILED" ? (
            <Tooltip title={t("Retry", { ns: "Integrations" })}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={retryingIds.has(row.id)}
                  onClick={() => handleRetry(row)}
                >
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null,
      },
    ],
    [t, contactTypeLabels, retryingIds, getFormattedDateTime]
  );

  return (
    <Stack direction="column" spacing={1} sx={{ height: "100%" }}>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: "center", flexWrap: "wrap" }}
      >
        <PdsSyncMultiChoiceFilter<PdsSyncEntityType>
          label={t("Type", { ns: "Integrations" })}
          value={entityTypes}
          onChange={setEntityTypes}
          options={[
            { value: "CONTACT", label: contactTypeLabels.INDIVIDUAL },
            { value: "COMPANY", label: contactTypeLabels.COMPANY },
          ]}
        />
        <PdsSyncMultiChoiceFilter<PdsSyncTaskStatus>
          label={t("Status", { ns: "Integrations" })}
          value={status}
          onChange={setStatus}
          options={HISTORY_STATUSES.map(value => ({
            value,
            label: STATUS_LABELS[value],
          }))}
        />
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <DebouncedSearchInput
            placeholder={t("Search", { ns: "Global" })}
            defaultValue={searchTerm}
            onChangeSearchTerm={setSearchTerm}
          />
        </Box>
      </Stack>

      {query.error ? (
        <ErrorMessage
          message={t("Could not load. Please try again.", {
            ns: "Integrations",
          })}
          button={
            <Button onClick={() => query.refetch()} variant="outlined">
              {t("Retry", { ns: "Integrations" })}
            </Button>
          }
        />
      ) : showEmpty ? (
        <InfoMessage
          message={t("No sync history yet.", { ns: "Integrations" })}
        />
      ) : showFilteredEmpty ? (
        <InfoMessage
          message={t("No matches for the current filters.", {
            ns: "Integrations",
          })}
        />
      ) : query.loading && items.length === 0 ? (
        <LoadingSpinner />
      ) : isMinTablet ? (
        <DataGrid<PdsSyncReviewTaskFragment>
          density="compact"
          sx={COMPACT_FOOTER_SX}
          localeText={{
            noRowsLabel: t("There are no items to display", { ns: "Global" }),
          }}
          loading={query.loading}
          hideFooter={totalCount === 0}
          columns={columns}
          rows={items}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
          rowCount={totalCount}
        />
      ) : (
        <Stack direction="column" spacing={1} sx={{ overflow: "auto" }}>
          {items.map(row => (
            <CardItem key={row.id}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "flex-start" }}
              >
                <Stack
                  direction="column"
                  spacing={0.5}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Typography variant="body2" className="sentry-mask">
                    {row.displayName ?? "—"}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    sx={{ flexWrap: "wrap", alignItems: "center" }}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        contactTypeLabels[
                          row.entityType === "CONTACT"
                            ? "INDIVIDUAL"
                            : "COMPANY"
                        ]
                      }
                    />
                    <Chip
                      size="small"
                      color={
                        row.status === "FAILED"
                          ? "error"
                          : row.status === "REJECTED"
                            ? "default"
                            : "success"
                      }
                      label={STATUS_LABELS[row.status]}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {getFormattedDateTime(row.updatedAt)}
                  </Typography>
                </Stack>
                {row.status === "FAILED" && (
                  <Tooltip title={t("Retry", { ns: "Integrations" })}>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={retryingIds.has(row.id)}
                        onClick={() => handleRetry(row)}
                      >
                        <ReplayIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </CardItem>
          ))}
          <TablePagination
            component="div"
            count={totalCount}
            page={paginationModel.page}
            onPageChange={(_event, newPage) =>
              setPaginationModel(pm => ({ ...pm, page: newPage }))
            }
            rowsPerPage={paginationModel.pageSize}
            onRowsPerPageChange={event =>
              setPaginationModel({
                pageSize: parseInt(event.target.value, 10),
                page: 0,
              })
            }
            labelRowsPerPage={t("Per page:", { ns: "Global" })}
          />
        </Stack>
      )}
    </Stack>
  );
}
